import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import {
  FormControl,
  Typography,
  Select,
  MenuItem,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Box,
  IconButton,
} from "@mui/material";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import CloseIcon from "@mui/icons-material/Close";
import { Auth } from "aws-amplify";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { v4 as uuidv4 } from "uuid";
import config from "../../../../aws-exports";
import dayjs from "dayjs";
import {
  fetchData,
  postQuery,
  uploadFileToS3Path,
  searchfetchQueries,
} from "../../../../services/BackendServices";
import AlertModal from "../../../Requirement/components/common/AlerModel";
import RemarksThreadKyc from "./components/RemarksThreadKyc";
import { useLoading } from "../../../../context/LoadingContext";
const BuyerKYC = () => {
  const [formData, setFormData] = useState({
    idType: "",
    expiryDate: "",
    selfieImage: null,
    tin: "",
    taxCountry: "",
    pepConfirm: false,
    verifyConfirm: false,
    Status: "",
  });
  const navigate = useNavigate();
  const [webcamActive, setWebcamActive] = useState(false);
  const [webcamDialogOpen, setWebcamDialogOpen] = useState(false);
  const [capturedImage, setCapturedImage] = useState(null);
  const { setLoading, loading } = useLoading();
  const [submitted, setSubmitted] = useState(false);
  const [countrymaster, setCountryMaster] = useState([]);
  const [isUpdateMode, setIsUpdateMode] = useState(false);
  const [userKycID, setUserKycID] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [activeUser, setActiveUser] = useState("");
  const [activeUserName, setActiveUserName] = useState("");
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [errors, setErrors] = useState({});
  const oneYearLater = dayjs().add(1, "year").format("YYYY-MM-DD");
  const [activeTab, setActiveTab] = useState(0);
  const bucket = process.env.REACT_APP_AMTPL_BUCKET;
  const bucketfolder = "public/media/amtpl";
  const [documentVaultUserFiles, setDocumentVaultUserFiles] = useState([]);
  const filePath = `${bucketfolder}/`;
  const fileUrl = `https://${bucket}.s3.${config.aws_project_region}.amazonaws.com/${filePath}`;
  const [modal, setModal] = useState({
    show: false,
    type: "info",
    title: "",
    message: "",
    nextAction: null,
    autoClose: null,
  });
  const handleTabChange = (event, newValue) => setActiveTab(newValue);
  const handleModalClose = () => {
    const action = modal.nextAction;
    setModal((prev) => ({ ...prev, show: false, nextAction: null }));
    if (action) action();
  };
  // Determine if form should read-only: Approved or (existing submitted and not in edit mode)
  const readOnly =
    formData.Status === "Approved" ||
    (isUpdateMode &&
      formData.Status &&
      formData.Status !== "Approved" &&
      !editMode);

  const loadCountries = useCallback(async () => {
    setLoading(true);
    try {
      const [allCountries] = await Promise.all([
        fetchData({
          target: "listDropDownOptions",
          userOrder: "Value",
          rowStart: 0,
          pageSize: 1000,
          filterTerms: { Type: "Country" },
        }),
      ]);
      if (allCountries.items.length > 0) {
        setCountryMaster(allCountries.items);
      } else {
        setCountryMaster([]);
      }
    } catch (error) {
      console.error("Error loading countries: ", error);
    } finally {
      setLoading(false);
    }
  }, []);
  const loadUserDocumentVault = useCallback(async () => {
    setLoading(true);
    try {
      const [allDocs] = await Promise.all([
        searchfetchQueries({
          target: "listUserDocument",
          userOrder: "CreatedAt DESC",
          rowStart: 0,
          pageSize: 1000,
          filterTerms: { CreatedBy: activeUser, Category: "KYC Document" },
        }),
      ]);
      console.log("All Documents:", allDocs);
      setDocumentVaultUserFiles(allDocs || []);
    } catch (error) {
      console.error("Error loading user document vault files:", error);
      setDocumentVaultUserFiles([]);
      setModal({
        show: true,
        type: "error",
        title: "Error",
        message: "Failed to load documents from vault.",
        autoClose: false,
      });
    } finally {
      setLoading(false);
    }
  }, [setLoading, activeUser, setModal]);

  // --- Fetch Existing KYC Data ---
  const fetchExistingKYC = useCallback(async () => {
    if (!activeUser) return;
    setLoading(true);
    try {
      const userKycSettings = await fetchData({
        target: "listUserKyc",
        userOrder: "UserKycID",
        rowStart: 0,
        pageSize: 1,
        filterTerms: { EmailID: activeUser },
      });

      if (userKycSettings.items && userKycSettings.items.length > 0) {
        const kycData = userKycSettings.items[0];
        setIsUpdateMode(true);
        setUserKycID(kycData.UserKycID);
        const newFormData = {
          idType: kycData.IdType || "",
          expiryDate: kycData.ExpiryDate
            ? kycData.ExpiryDate.split("T")[0]
            : "",
          selfieImage: kycData.LiveSelfiePhoto
            ? { name: kycData.LiveSelfiePhoto }
            : null,
          tin: kycData.TinTaxIDNumber || "",
          taxCountry: kycData.TaxResidencyCountry || "",
          pepConfirm: true,
          verifyConfirm: true,
          Status: kycData.Status || "",
        };
        setFormData(newFormData);
        if (
          kycData.LiveSelfiePhoto &&
          !(newFormData.selfieImage instanceof File)
        ) {
          setCapturedImage(`${fileUrl}${kycData.LiveSelfiePhoto}`);
        }
        setSubmitted(kycData.Status !== "Pending");
        setEditMode(false);
      } else {
        setIsUpdateMode(false);
        setUserKycID(null);
        setEditMode(false);
      }
    } catch (error) {
      console.error("Error fetching KYC data:", error);
      alert("Failed to load existing KYC data. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [activeUser, fileUrl]);

  useEffect(() => {
    loadCountries();
  }, [loadCountries]);

  useEffect(() => {
    const checkUser = async () => {
      try {
        const user = await Auth.currentAuthenticatedUser();
        const token = user.signInUserSession.idToken.jwtToken;
        const decodedToken = jwtDecode(token);
        const groups = decodedToken["cognito:groups"] || [];
        if (!groups.includes("AMTPL_GLOBAL_PARTNERS")) {
          await Auth.signOut();
          navigate("/login/global-partner");
        } else {
          const email = user.attributes.email;
          const usrname = user.attributes.given_name;
          setActiveUser(email);
          setActiveUserName(usrname);
        }
      } catch (error) {
        console.error("Error checking user:", error);
        await Auth.signOut();
        navigate("/login/global-partner");
      }
    };
    checkUser();
  }, [navigate]);

  useEffect(() => {
    if (activeUser) {
      Promise.all([fetchExistingKYC(), loadUserDocumentVault()]);
    }
  }, [activeUser, fetchExistingKYC, loadUserDocumentVault]);

  useEffect(() => {
    if (
      !isUpdateMode &&
      documentVaultUserFiles.length > 0 &&
      formData.idType === ""
    ) {
      console.log(documentVaultUserFiles, "documentVaultUserFiles");
      setFormData((prev) => ({
        ...prev,
        idType: documentVaultUserFiles[0].DocumentName || "",
      }));
      console.log(formData);
    }
  }, [isUpdateMode, documentVaultUserFiles]);

  const progress = useMemo(() => {
    const requiredFields = [
      formData.idType,
      formData.selfieImage,
      formData.verifyConfirm,
    ];
    const optionalFields = [
      formData.expiryDate,
      formData.tin,
      formData.taxCountry,
      formData.pepConfirm,
    ];

    const requiredCompleted = requiredFields.filter((field) =>
      typeof field === "boolean" ? field : !!field
    ).length;
    const optionalCompleted = optionalFields.filter((field) =>
      typeof field === "boolean" ? field : !!field
    ).length;

    return ((requiredCompleted + optionalCompleted) / 7) * 100;
  }, [formData]);

  // --- Validation Logic ---
  const validateField = (name, value) => {
    let error = "";
    switch (name) {
      case "idType":
        if (!value) error = "This field is required";
        break;
      // case "selfieImage":
      //     if (!value) error = "This field is required";
      //     break;
      // case "verifyConfirm":
      //     if (!value) error = "This field is required";
      //     break;
      // case "tin":
      //     // optional
      //     break;
      // case "taxCountry":
      //     // optional
      //     break;
      case "expiryDate":
        if (value && dayjs(value).isBefore(dayjs()))
          error = "Expiry date cannot be in the past";
        break;
      default:
        break;
    }
    return error;
  };

  const validateAllFields = () => {
    const newErrors = {};
    Object.keys(formData).forEach((key) => {
      if (["idType", "selfieImage", "verifyConfirm"].includes(key)) {
        const error = validateField(key, formData[key]);
        if (error) newErrors[key] = error;
      }
    });
    setErrors(newErrors);
    return newErrors;
  };

  const handleChange = (e) => {
    if (readOnly) return;
    const { name, value, type, checked } = e.target;
    let newValue;
    if (type === "checkbox") {
      newValue = checked;
    } else {
      newValue = value;
    }
    setFormData((prev) => ({
      ...prev,
      [name]: newValue,
    }));
    const fieldError = validateField(name, newValue);
    setErrors((prev) => ({
      ...prev,
      [name]: fieldError || undefined,
    }));
  };

  const handleFileChange = (e) => {
    if (readOnly) return;
    const { name, files } = e.target;
    if (files && files[0]) {
      setFormData((prev) => ({
        ...prev,
        [name]: files[0],
      }));
      const fieldError = validateField(name, files[0]);
      setErrors((prev) => ({
        ...prev,
        [name]: fieldError || undefined,
      }));
    }
  };

  const handleFileRemove = (name) => {
    if (readOnly) return;
    setFormData((prev) => ({ ...prev, [name]: null }));
    const fieldError = validateField(name, null);
    setErrors((prev) => ({
      ...prev,
      [name]: fieldError || undefined,
    }));
  };

  const handleBlur = (e) => {
    if (readOnly) return;
    const { name, value } = e.target;
    const fieldError = validateField(name, value);
    setErrors((prev) => ({
      ...prev,
      [name]: fieldError || undefined,
    }));
  };

  const startWebcam = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        setWebcamActive(true);
      }
    } catch (err) {
      console.error("Error accessing webcam:", err);
      alert("Failed to access webcam. Please ensure permissions are granted.");
      setWebcamDialogOpen(false);
      setLoading(false);
    }
  };

  const stopWebcam = () => {
    if (videoRef.current && videoRef.current.srcObject) {
      const stream = videoRef.current.srcObject;
      stream.getTracks().forEach((track) => track.stop());
      videoRef.current.srcObject = null;
    }
    setLoading(false);
    setWebcamActive(false);
  };

  const capturePhoto = () => {
    const context = canvasRef.current.getContext("2d");
    context.drawImage(videoRef.current, 0, 0, 640, 480);
    canvasRef.current.toBlob((blob) => {
      const file = new File([blob], "selfie.jpg", { type: "image/jpeg" });
      const imageUrl = URL.createObjectURL(blob);
      setFormData((prev) => ({
        ...prev,
        selfieImage: file,
      }));
      setCapturedImage(imageUrl);
      const fieldError = validateField("selfieImage", file);
      setErrors((prev) => ({
        ...prev,
        selfieImage: fieldError || undefined,
      }));
      stopWebcam();
      setWebcamDialogOpen(false);
      setLoading(false);
    }, "image/jpeg");
  };

  useEffect(() => {
    if (webcamDialogOpen && !webcamActive) {
      startWebcam();
    }
    return () => stopWebcam();
  }, [webcamDialogOpen]);

  const removeSelfie = () => {
    setFormData((prev) => ({ ...prev, selfieImage: null }));
    setCapturedImage(null);
    const fieldError = validateField("selfieImage", null);
    setErrors((prev) => ({
      ...prev,
      selfieImage: fieldError || undefined,
    }));
  };

  const handleSubmit = async () => {
    if (readOnly) return;
    if (!activeUser) {
      alert("User email not available. Please log in again.");
      return;
    }
    const validationErrors = validateAllFields();
    if (Object.keys(validationErrors).length > 0) {
      // alert('Please fill in all required fields and confirm identity verification.');
      setModal({
        show: true,
        type: "error",
        title: "Error!",
        message:
          "Please fill in all required fields and confirm identity verification.",
        autoClose: false,
      });
      return;
    }

    setLoading(true);
    try {
      const currentUserKycID = isUpdateMode ? userKycID : uuidv4();
      let liveSelfiePhotoRef =
        formData.selfieImage instanceof File
          ? ""
          : formData.selfieImage
          ? formData.selfieImage.name
          : "";

      if (formData.selfieImage instanceof File) {
        const result = await uploadFileToS3Path(
          formData.selfieImage,
          bucketfolder
        );
        liveSelfiePhotoRef = result.fileName;
      }

      const userKycInput = {
        UserKycID: currentUserKycID,
        EmailID: activeUser,
        IdType: formData.idType,
        IdNumber: documentVaultUserFiles[0].DocumentId || null,
        UploadFrontOfID: documentVaultUserFiles[0].DocumentFile || null,
        ExpiryDate: formData.expiryDate || oneYearLater,
        LiveSelfiePhoto: liveSelfiePhotoRef,
        TinTaxIDNumber: formData.tin || "",
        TaxResidencyCountry: formData.taxCountry || "",
        Status: "Pending",
      };

      if (isUpdateMode) {
        await postQuery({
          target: "updateUserKyc",
          userKycInput: {
            ...userKycInput,
            Status: formData.Status || "Pending",
          },
        });
      } else {
        await postQuery({
          target: "createUserKyc",
          userKycInput: {
            ...userKycInput,
            Status: "Pending",
          },
        });
        setUserKycID(currentUserKycID);
        setIsUpdateMode(true);
      }

      setSubmitted(true);
      setEditMode(false);
      setModal({
        show: true,
        type: "success",
        title: "Success!",
        message: isUpdateMode
          ? "KYC updated successfully."
          : "KYC submitted successfully.",
        autoClose: false,
      });
    } catch (error) {
      console.error("Error submitting KYC:", error);
      //alert('Error submitting KYC: ' + (error.message || 'Unknown error'));
      setModal({
        show: true,
        type: "error",
        title: "Error!",
        message: "Failed. Please try again.",
        autoClose: false,
      });
    } finally {
      setLoading(false);
    }
  };

  const renderFilePreview = (file, name) => (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        backgroundColor: "white",
        border: "1px solid #e0e0e0",
        borderRadius: 1,
        p: 1,
        mt: 1,
      }}
    >
      <Typography
        variant="body2"
        sx={{
          flex: 1,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {file instanceof File ? (
          file.name
        ) : (
          <a
            target="_blank"
            rel="noopener noreferrer"
            href={`${fileUrl}${file.name}`}
          >
            {file.name}
          </a>
        )}
      </Typography>
      <IconButton
        size="small"
        onClick={() => handleFileRemove(name)}
        disabled={readOnly}
        sx={{ ml: 1 }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>
  );

  const getStatusBadge = (status) => {
    let bgColor = "",
      textColor = "";
    switch (status) {
      case "Pending":
        bgColor = "#fef3c7";
        textColor = "#b45309";
        break;
      case "Approved":
        bgColor = "#d1fae5";
        textColor = "#065f46";
        break;
      default:
        bgColor = "#f3f4f6";
        textColor = "#374151";
    }
    return (
      <Box
        sx={{
          backgroundColor: bgColor,
          color: textColor,
          padding: "4px 10px",
          borderRadius: "999px",
          fontSize: "0.75rem",
          fontWeight: 500,
          display: "inline-block",
        }}
      >
        {status === "Pending" ? "In Review" : status}
      </Box>
    );
  };

  const handleVisibilityClick = async (doc) => {
    if (doc.DocumentFile) {
      try {
        setLoading(true);
        console.log(
          `Requesting secure document access for: ${doc.DocumentFile}`
        );

        // Request document through secure Lambda endpoint
        const response = await postQuery(
          {
            target: "getSecureDocument",
            Input: {
              bucketName: process.env.REACT_APP_AMTPL_BUCKET,
              fileName: doc.DocumentFile,
              documentId: doc.DocumentId,
            },
          },
          "backendops"
        );

        if (response && response.fileContent) {
          // Convert base64 to blob and open in new tab
          const byteCharacters = atob(response.fileContent);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);
          const blob = new Blob([byteArray], {
            type: response.contentType || "application/pdf",
          });
          const blobUrl = URL.createObjectURL(blob);

          // Open in new tab
          window.open(blobUrl, "_blank");

          // Clean up blob URL after a delay
          setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
        } else {
          throw new Error("Unable to retrieve document content");
        }
      } catch (error) {
        console.error(`Error accessing document:`, error);
        setModal({
          show: true,
          type: "error",
          title: "Access Denied",
          message:
            "Unable to access document. You may not have permission to view this file.",
          autoClose: false,
        });
      } finally {
        setLoading(false);
      }
    } else {
      console.error(`Document file is missing for document ${doc.id}`);
      setModal({
        show: true,
        type: "error",
        title: "Error",
        message: "Unable to open document. File reference is missing.",
        autoClose: false,
      });
    }
  };

  return (
    <div className="container kyc-verification mt-4">
      <AlertModal
        type={modal.type}
        title={modal.title}
        message={modal.message}
        showModal={modal.show}
        onClose={handleModalClose}
        buttonText="OK"
        autoClose={modal.autoClose}
      />
      <div className="card p-2">
        <style>
          {`
                        .form-check-label {
                            margin-left: 8px;
                        }
                        .form-check-input:checked {
                            background-color: #ff9800;
                            border-color: #ff9800;
                        }
                        .form-check-input:focus {
                            border-color: #ff9800;
                            box-shadow: 0 0 0 0.2rem rgba(255, 152, 0, 0.25);
                        }
                    `}
        </style>
        <div
          className="margin-bottom-10"
          style={{ marginBottom: "10px !important" }}
        ></div>
        {isUpdateMode && formData.Status && (
          <div className="row mb-3">
            <div className="col-12">
              <div
                className="d-flex justify-content-between align-items-center p-2"
                style={{
                  backgroundColor:
                    formData.Status === "Approved" ? "#d1fae573" : "#fef3c750",
                  color: formData.Status === "Approved" ? "#065f46" : "#b45309",
                  borderLeft:
                    formData.Status === "Approved"
                      ? "3px solid #00b658fb"
                      : "3px solid #b69203f7",
                  borderRadius: "5px",
                }}
              >
                <span
                  className="p-1 px-2 rounded"
                  style={{ fontSize: "0.875rem" }}
                >
                  Current Status: {getStatusBadge(formData.Status)}
                </span>
              </div>
            </div>
          </div>
        )}
        <div
          className="table-header "
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: 5,
          }}
        >
          <div className="header-text">
            <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
              <div className="head-text">KYC Verification</div>
            </div>
            <div className="head-subtext" style={{ color: "#666" }}>
              Complete the following steps to verify your identity.
            </div>
          </div>
          <div
            className="submit-btn-form"
            style={{ display: "flex", gap: "10px" }}
          >
            {isUpdateMode &&
            formData.Status &&
            formData.Status !== "Approved" ? (
              !editMode ? (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => setEditMode(true)}
                  disabled={loading}
                >
                  Edit
                </Button>
              ) : (
                <>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSubmit}
                    disabled={loading}
                  >
                    {loading ? "Submitting..." : "Submit"}
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={() => {
                      setEditMode(false);
                      fetchExistingKYC();
                    }}
                    disabled={loading}
                  >
                    Cancel
                  </Button>
                </>
              )
            ) : !isUpdateMode ? (
              <Button
                variant="contained"
                color="primary"
                onClick={handleSubmit}
                disabled={loading || submitted}
              >
                {loading ? "Submitting..." : submitted ? "Submitted" : "Submit"}
              </Button>
            ) : null}
          </div>
        </div>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{ mb: 2, "& .MuiTabs-indicator": { display: "none" } }}
        >
          <Tab
            label="KYC Information"
            sx={{
              borderBottom: "2px solid #de6b14 !important",
              "&:not(.Mui-selected)": {
                borderBottom: "none",
                border: "none",
              },
              "&.Mui-selected": {
                backgroundColor: "#de6b14 !important",
                color: "white",
                borderBottom: "2px solid #de6b14 !important",
                border: "none",
              },
            }}
          />

          <Tab
            label="Remarks"
            disabled={!isUpdateMode || formData.Status === "Approved"}
            sx={{
              borderBottom: "2px solid #de6b14 !important",
              "&:not(.Mui-selected)": {
                borderBottom: "none",
                border: "none",
              },
              "&.Mui-selected": {
                backgroundColor: "#de6b14 !important",
                color: "white",
                borderBottom: "2px solid #de6b14 !important",
                border: "none",
              },
              "&.Mui-disabled": {
                color: "rgba(0, 0, 0, 0.38)",
              },
            }}
          />
        </Tabs>
        <div className="mt-4 p-3 border rounded bg-light">
          {activeTab === 0 && (
            <div
              className="form-sec "
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "20px",
                padding: "10px",
              }}
            >
              <div className="row">
                {documentVaultUserFiles.length < 1 && (
                  <div className="col-md-12">
                    <div
                      className="d-flex justify-content-between align-items-center p-2"
                      style={{
                        backgroundColor: "#fef3c750",
                        color: "#b45309",
                        marginBottom: "10px",
                        borderLeft: "3px solid #b69203f7",
                        borderRadius: "5px",
                      }}
                    >
                      <span
                        className="p-1 px-2 rounded"
                        style={{ fontSize: "0.875rem" }}
                      >
                        Please update your KYC document in "Document Vault"
                        section for submitting KYC for verification.
                      </span>
                    </div>
                  </div>
                )}
                <div className="col-md-6">
                  <FormControl fullWidth error={!!errors.idType}>
                    <Typography variant="body2">Id Type *</Typography>
                    <TextField
                      name="idType"
                      value={formData.idType}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      type="text"
                      variant="outlined"
                      InputLabelProps={{ shrink: true }}
                      disabled={true}
                      sx={{ backgroundColor: readOnly ? "#f5f5f5" : "white" }}
                      error={!!errors.idType}
                      helperText={errors.idType}
                    />
                    {/* {errors.idType && (
                      <Typography variant="caption" color="error">
                        {errors.idType}
                      </Typography>
                    )} */}
                  </FormControl>
                </div>
                <div className="col-md-6">
                  <FormControl fullWidth>
                    <Typography variant="body2">KYC Document</Typography>
                    {documentVaultUserFiles.length > 0 ? (
                      <div
                        style={{
                          border: "1px solid #ccc",
                          borderRadius: 5,
                          p: 1,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "start",
                          cursor: "pointer",
                          backgroundColor: "white",
                          height: "40px",
                          color: "#ccc",
                          "&:hover": {
                            backgroundColor: "#f5f5f5",
                          },
                          width: "100%",
                        }}
                      >
                        <div
                          component="button"
                          onClick={() =>
                            handleVisibilityClick(documentVaultUserFiles[0])
                          }
                        >
                          <InsertDriveFileIcon />{" "}
                          {documentVaultUserFiles[0].DocumentFile}
                        </div>
                      </div>
                    ) : (
                      <Typography variant="body2" color="textSecondary">
                        No KYC Documents found.
                      </Typography>
                    )}
                  </FormControl>
                </div>
              </div>

              <div className="row">
                <div className="col-md-6">
                  <FormControl fullWidth>
                    <Typography variant="body2">Expiry Date</Typography>
                    <TextField
                      name="expiryDate"
                      value={formData.expiryDate}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      type="date"
                      variant="outlined"
                      InputLabelProps={{ shrink: true }}
                      disabled={readOnly}
                      sx={{ backgroundColor: readOnly ? "#f5f5f5" : "white" }}
                      error={!!errors.expiryDate}
                      helperText={errors.expiryDate}
                    />
                  </FormControl>
                </div>
              </div>

              <div className="row">
                <div className="col-md-6">
                  <FormControl fullWidth>
                    <Typography variant="body2">
                      Live Photo / Selfie Verification *
                    </Typography>
                    <Typography
                      variant="body2"
                      style={{ color: "#666", marginTop: "8px" }}
                    >
                      To match user's selfie with uploaded ID.
                    </Typography>
                    <Button
                      variant="contained"
                      color="warning"
                      onClick={() => {
                        setWebcamDialogOpen(true);
                      }}
                      disabled={readOnly}
                      style={{ marginTop: "8px", width: "70%" }}
                    >
                      Capture live selfie photo
                    </Button>
                    <div className="sub-t">
                      Option to "Take a Photo" using device camera (Web)
                    </div>
                    {errors.selfieImage && !formData.selfieImage && (
                      <Typography
                        variant="caption"
                        color="error"
                        sx={{ mt: 1 }}
                      >
                        {errors.selfieImage}
                      </Typography>
                    )}
                  </FormControl>
                </div>

                {capturedImage && (
                  <div
                    className="col-md-6"
                    style={{
                      display: "flex",
                      justifyContent: "start",
                      alignItems: "center",
                    }}
                  >
                    <img
                      src={capturedImage}
                      alt="Captured Selfie"
                      style={{
                        width: "120px",
                        height: "100px",
                        marginTop: "8px",
                        borderRadius: 6,
                        border: "1px solid #ccc",
                      }}
                    />
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        ml: 1,
                      }}
                    >
                      <IconButton
                        size="small"
                        onClick={removeSelfie}
                        disabled={readOnly}
                        sx={{ alignSelf: "flex-start" }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                      <Button
                        variant="text"
                        color="primary"
                        onClick={() => {
                          setFormData((prev) => ({
                            ...prev,
                            selfieImage: null,
                          }));
                          setCapturedImage(null);
                          setWebcamDialogOpen(true);
                          setLoading(true);
                        }}
                        size="small"
                        sx={{
                          textTransform: "none",
                          height: "20px",
                          width: "120px",
                        }}
                        disabled={readOnly}
                      >
                        Reupload / Recapture Selfie
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              <div className="mt-4 p-3 border rounded bg-light">
                <div className="row">
                  <label htmlFor="taxCountry" className="form-label fw-bold">
                    Tax Information
                  </label>
                  <div className="col-md-12">
                    <div className="form-group mb-3">
                      <Typography variant="h6" gutterBottom>
                        TIN / Tax ID Number
                      </Typography>

                      <input
                        type="text"
                        className="form-control"
                        id="tin"
                        name="tin"
                        placeholder="Enter TIN / Tax ID Number"
                        value={formData.tin}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        disabled={readOnly}
                      />
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-6">
                    <Typography variant="h6" gutterBottom>
                      Tax Residency Country
                    </Typography>

                    <Select
                      id="taxCountry"
                      name="taxCountry"
                      value={formData.taxCountry}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      inputProps={{ "aria-label": "Select Country" }}
                      fullWidth
                      disabled={readOnly}
                    >
                      <MenuItem value="" disabled>
                        Select country
                      </MenuItem>
                      {countrymaster.map((p) => (
                        <MenuItem key={p.ID} value={p.Value}>
                          {p.Value}
                        </MenuItem>
                      ))}
                    </Select>
                  </div>
                </div>

                <div className="form-group mb-3"></div>

                <div className="form-check mb-2">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="pepConfirm"
                    name="pepConfirm"
                    checked={formData.pepConfirm}
                    onChange={handleChange}
                    disabled={readOnly}
                  />
                  <label className="form-check-label" htmlFor="pepConfirm">
                    I confirm the information is accurate and I’m not a
                    politically exposed person (PEP).
                  </label>
                </div>
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="verifyConfirm"
                    name="verifyConfirm"
                    checked={formData.verifyConfirm}
                    onChange={handleChange}
                    disabled={readOnly}
                  />
                  <label className="form-check-label" htmlFor="verifyConfirm">
                    I authorize verification of my identity for compliance
                    purposes. *
                  </label>
                  {errors.verifyConfirm && (
                    <Typography
                      variant="caption"
                      color="error"
                      sx={{ display: "block" }}
                    >
                      {errors.verifyConfirm}
                    </Typography>
                  )}
                </div>
              </div>
            </div>
          )}
          {activeTab === 1 && isUpdateMode && (
            <Box className="tab-content">
              <RemarksThreadKyc
                kycId={userKycID}
                currentUserId={activeUser}
                currentUserName={activeUserName}
              />
            </Box>
          )}
        </div>

        {/* Webcam Capture Modal */}
        <Dialog
          open={webcamDialogOpen}
          onClose={() => {
            stopWebcam();
            setWebcamDialogOpen(false);
            setLoading(false);
          }}
          aria-labelledby="webcam-dialog-title"
          fullWidth
        >
          <DialogTitle>Capture Selfie</DialogTitle>
          <DialogContent>
            <video
              ref={videoRef}
              autoPlay
              style={{ width: "100%", height: "auto", marginTop: "8px" }}
            />
            <canvas
              ref={canvasRef}
              style={{ display: "none" }}
              width="640"
              height="480"
            />
          </DialogContent>
          <DialogActions>
            <button
              className="submit-btn-more"
              onClick={() => {
                capturePhoto();
              }}
            >
              Capture Photo
            </button>
            <button
              className="submit-btn-more"
              onClick={() => {
                stopWebcam();
                setWebcamDialogOpen(false);
                setLoading(false);
              }}
            >
              Cancel
            </button>
          </DialogActions>
        </Dialog>
      </div>
    </div>
  );
};

export default BuyerKYC;
